import { FC, ReactElement } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

import Home from 'dibs-icons/exports/Home';
import Gallery from 'dibs-icons/exports/Gallery';
import Heart from 'dibs-icons/exports/HeartOutlined';

import { NavTabList } from 'dibs-elements/exports/NavTabList';
import { NavTab } from 'dibs-elements/exports/NavTab';
import { PAGE_TYPE_FAVORITES, PAGE_TYPE_PROJECTS, PAGE_TYPE_OVERVIEW } from '../constants';

import type { PageType } from '../constants';
import type { ProfileTabs_tradeFirm as TradeFirmType } from './__generated__/ProfileTabs_tradeFirm.graphql';

import styles from './ProfileTabs.scss';

type ProfileTabProps = {
    isEditMode: boolean;
    count?: number;
    icon?: ReactElement;
    type: PageType;
    label: ReactElement;
};

export const ProfileTab: FC<ProfileTabProps> = props =>
    !props.isEditMode && !props.count && props.type !== PAGE_TYPE_OVERVIEW ? null : (
        <div data-tn={`profile-tab-${props.type}`} className={styles.tabWrapper}>
            {props.count ? <div className={styles.tabCount}>{props.count}</div> : props.icon}
            <div className={styles.tabLabel}>{props.label}</div>
        </div>
    );

type Props = {
    onChangeTab: (pageType: PageType) => void;
    currentTab: PageType;
    isEditMode: boolean;
    tradeFirm: TradeFirmType;
};

export const ProfileTabsComponent: FC<Props> = props => {
    const { isEditMode, currentTab, onChangeTab, tradeFirm } = props;
    const projectCount = tradeFirm?.publicProfile?.designProjectsCount || 0;
    const favoriteItemCount = tradeFirm?.publicProfile?.portfolios?.[0]?.itemCount || 0;
    const urlLabel = tradeFirm?.publicProfile?.urlLabel || '';

    return (
        <div className={`colXl5 colXs12 ${styles.wrapper}`}>
            <NavTabList
                activeTab={currentTab}
                setActiveTab={(newTab: string) => onChangeTab(newTab as PageType)}
            >
                <NavTab
                    name={PAGE_TYPE_OVERVIEW}
                    href={`/design-firms/${urlLabel}/`}
                    onClick={e => e.preventDefault()}
                >
                    <ProfileTab
                        isEditMode={isEditMode}
                        type={PAGE_TYPE_OVERVIEW}
                        label={
                            <FormattedMessage
                                id="abt.designProfile.header.tab.overview"
                                defaultMessage="Profile"
                            />
                        }
                        icon={<Home className={styles.tabIcon} />}
                    />
                </NavTab>
                <NavTab
                    name={PAGE_TYPE_PROJECTS}
                    href={`/design-firms/${urlLabel}/`}
                    onClick={e => e.preventDefault()}
                >
                    <ProfileTab
                        isEditMode={isEditMode}
                        type={PAGE_TYPE_PROJECTS}
                        label={
                            <FormattedMessage
                                id="abt.designProfile.header.tab.projects"
                                defaultMessage="Projects"
                            />
                        }
                        icon={<Gallery className={styles.tabIcon} />}
                        count={projectCount}
                    />
                </NavTab>
                <NavTab
                    name={PAGE_TYPE_FAVORITES}
                    href={`/design-firms/${urlLabel}/`}
                    noFollow
                    onClick={e => e.preventDefault()}
                >
                    <ProfileTab
                        isEditMode={isEditMode}
                        type={PAGE_TYPE_FAVORITES}
                        label={
                            <FormattedMessage
                                id="abt.designProfile.header.tab.favorites"
                                defaultMessage="Favorites"
                            />
                        }
                        icon={<Heart className={styles.tabIcon} />}
                        count={favoriteItemCount}
                    />
                </NavTab>
            </NavTabList>
        </div>
    );
};

export const ProfileTabs = createFragmentContainer(ProfileTabsComponent, {
    tradeFirm: graphql`
        fragment ProfileTabs_tradeFirm on TradeFirmType {
            publicProfile {
                urlLabel
                designProjectsCount
                portfolios {
                    itemCount
                }
            }
        }
    `,
});
